import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  getMeControllerObterQueryKey,
  useMeControllerConcluirSerproId,
} from '@/api/generated/me/me';
import { ApiError } from '@/api/http-client';
import { ROTAS } from '@/shared/lib/rotas';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { toast } from '@/shared/ui/sonner';

/**
 * A volta do Serpro: o médico aprovou (ou recusou) no app SerproID e o Serpro o
 * devolveu para esta URL com `code` e `state` na query — ou `error`, quando ele
 * recusou.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  A tela só repassa o que veio na URL para a API, que troca o `code` pelo
 *  token da sessão. Ela não decide nada nem interpreta o `code`.
 *
 *  ⚠️ O `code` vale 60 SEGUNDOS e é de USO ÚNICO. Por isso a chamada sai
 *  exatamente uma vez — o `useRef` segura o segundo disparo do StrictMode, que
 *  montaria o efeito duas vezes e faria a segunda troca falhar com
 *  "autorização já usada" logo depois de a primeira ter dado certo.
 *
 *  ⚠️ O caminho dela é `ROTAS.perfilSerproId`, cadastrado como URL de retorno
 *  da aplicação no Serpro. Não renomear sem o backend.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function SerproIdRetornoPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const concluir = useMeControllerConcluirSerproId();

  const jaDisparou = useRef(false);
  const [falha, setFalha] = useState<string | null>(null);

  useEffect(() => {
    if (jaDisparou.current) return;
    jaDisparou.current = true;

    const state = params.get('state');
    const code = params.get('code');
    const erro = params.get('error');

    // Sem `state` não há a que ida ligar a volta — nem vale gastar a chamada.
    if (!state) {
      setFalha('O retorno do Serpro veio incompleto. Peça uma nova autorização no perfil.');
      return;
    }

    // `mutateAsync` em vez dos callbacks do `mutate`: estes se perdem se o
    // componente desmontar no meio, e a resposta é o que atualiza a sessão.
    concluir
      .mutateAsync({ data: { state, ...(code ? { code } : {}), ...(erro ? { erro } : {}) } })
      .then((atualizado) => {
        queryClient.setQueryData(getMeControllerObterQueryKey(), atualizado);
        toast.success('SerproID configurado. Os próximos documentos saem assinados digitalmente.');
        // `replace`: o botão "voltar" não pode trazer de volta uma URL com um
        // `code` que já foi gasto.
        navigate(ROTAS.perfil, { replace: true });
      })
      .catch((e: unknown) => {
        setFalha(
          e instanceof ApiError ? e.message : 'Não foi possível concluir a autorização do SerproID.',
        );
      });
    // Uma vez só, de propósito — ver o cabeçalho. `concluir`, `params`, etc.
    // mudam de identidade a cada render e não podem reabrir esta chamada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto max-w-lg py-12">
      <Card className="bg-surface-raised border-2 border-brand/20">
        <CardContent className="space-y-4 p-6">
          {falha ? (
            <>
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">Não foi possível concluir</p>
                  <p className="text-sm text-muted-foreground">{falha}</p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="button" onClick={() => navigate(ROTAS.perfil, { replace: true })}>
                  Voltar ao perfil
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-3" role="status">
              <Loader2 className="w-5 h-5 animate-spin text-brand" />
              <p className="text-sm text-foreground">Concluindo a autorização do SerproID…</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
